import { useState, useEffect, useCallback } from 'react'

/**
 * PersistedTextarea component with localStorage persistence.
 * Saves and restores textarea value between page refreshes.
 * Storage key is user-specific to avoid data leakage.
 *
 * @param {Object} props - Component props
 * @param {string} props.value - Current textarea value (controlled)
 * @param {Function} props.onChange - Callback when value changes
 * @param {string} props.storageKey - localStorage key for persistence
 * @param {string} [props.placeholder] - Placeholder text
 * @param {boolean} [props.isDisabled] - Whether textarea is disabled
 * @param {number} [props.maxLength] - Maximum character length (default 10000)
 * @returns {React.ReactElement} Persisted textarea with character counter
 */
export default function PersistedTextarea({
  value,
  onChange,
  storageKey,
  placeholder = '',
  isDisabled = false,
  maxLength = 10000,
}) {
  const [charCount, setCharCount] = useState(0)
  const [lineCount, setLineCount] = useState(0)

  // Update counters when value changes
  useEffect(() => {
    setCharCount(value.length)
    setLineCount(value.split('\n').length)
  }, [value])

  // Save to localStorage on change (with debounce via blur)
  const handleBlur = useCallback(() => {
    try {
      if (storageKey) {
        localStorage.setItem(storageKey, value)
      }
    } catch (error) {
      // Handle localStorage quota errors silently
      console.warn('Failed to save to localStorage:', error)
    }
  }, [storageKey, value])

  // Handle change event
  const handleChange = useCallback(
    (event) => {
      onChange(event.target.value)
    },
    [onChange]
  )

  return (
    <div className="persisted-textarea">
      <textarea
        className="persisted-textarea__input"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={isDisabled}
        maxLength={maxLength}
        rows={10}
        aria-label="Pole tekstowe do importu fiszek"
        aria-describedby="textarea-counter"
      />
      <div
        id="textarea-counter"
        className="persisted-textarea__counter"
        aria-live="polite"
      >
        <span>
          {lineCount} {lineCount === 1 ? 'linia' : 'linii'}
        </span>
        <span className="persisted-textarea__separator">•</span>
        <span>
          {charCount} / {maxLength} znaków
        </span>
      </div>
    </div>
  )
}
